import React, { useState, useCallback, Fragment, useEffect } from 'react';
import { translate } from '../../Services/translation';
import { useSelector, useDispatch } from 'react-redux';
import { toggle } from '../../Actions/Cart.action';
import {
  update,
  removeRange,
  load,
} from '../../Actions/Rutab/RutabCart.action';
import { reloadPayment } from '../../Actions/Checkout.action';
import { debounce } from 'lodash-es';
import { removeCartItem } from '../../Actions/Rutab/DecalEditor.action';

const mapCartToAbort = {};

const quantityDispatch = debounce((articleNumber, quantity, dispatch) => {
  mapCartToAbort[articleNumber] = new AbortController();
  dispatch(update(articleNumber, quantity, mapCartToAbort[articleNumber]))
    .then(() => dispatch(reloadPayment()))
    .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
}, 500);

const MiniCart = () => {
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const { quantity, orderTotal, checkoutUrl, showInfo } = useSelector(
    (state) => state.cart
  );

  const [orderRows, setOrderRows] = useState(cart?.orderRows);
  const [sortingRows, setSortingRows] = useState(cart?.sortingRows);
  const [decalRows, setDecalRows] = useState(cart?.orderRows);
  const [removingRow, setRemovingRow] = useState({});
  const [removingSorting, setRemoveSorting] = useState({});
  const [editingRowSystemId, setEditingRowSystemId] = useState(null);
  const [itemAddedAnimation, setItemAddedAnimation] = useState(false);

  const handleCartUpdate = useCallback(() => {
    handleCartItemAdded();
    dispatch(load());
  }, [dispatch]);

  const handleCartItemAdded = useCallback(() => {
    const cartIcon = document.querySelector('.cart__icon');
    if (cartIcon) {
      cartIcon.classList.add('cart-icon-animated');

      setTimeout(() => {
        cartIcon.classList.remove('cart-icon-animated');
      }, 1000);
    }

    setItemAddedAnimation(true);

    setTimeout(() => {
      setItemAddedAnimation(false);
    }, 1000);
  }, []);

  useEffect(() => {
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('cartItemAdded', handleCartItemAdded);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('cartItemAdded', handleCartItemAdded);
    };
  }, [handleCartUpdate, handleCartItemAdded]);

  useEffect(() => {
    setOrderRows(cart?.orderRows.filter((row) => !row.isDecalItem));
    setSortingRows(cart?.sortingRows);
    setDecalRows(cart?.orderRows.filter((row) => row.isDecalItem));

    const params = new URLSearchParams(window.location.search);
    const editingRowId = params.get('id');
    const editingSortingId = params.get('sorting');
    if (editingRowId) {
      setEditingRowSystemId(editingRowId);
    }
    if (editingSortingId) {
      setEditingRowSystemId(editingSortingId);
    }

    return () => {
      setEditingRowSystemId(null);
    };
  }, [cart]);

  const onToggle = () => dispatch(toggle());

  const removeOrderRow = useCallback(
    (order) => {
      if (order.isDecalItem) {
        const resetCanvas = order.rowSystemId === editingRowSystemId;
        dispatch(removeCartItem(order.rowSystemId, resetCanvas)).then(() => dispatch(reloadPayment()));
      } else {
        dispatch(update(order.articleNumber, 0)).then(() => dispatch(reloadPayment()));
      }
    },
    [dispatch, editingRowSystemId]
  );

  const RemoveBtn = useCallback(
    ({ order }) => {
      return (
        <Fragment>
          {!removingRow[order.rowSystemId] && (
            <a
              className="mini__icon mini__icon--delete"
              onClick={() =>
                setRemovingRow({
                  ...removingRow,
                  [order.rowSystemId]: true,
                })
              }
              title={translate('general.remove')}
            ></a>
          )}
          {removingRow[order.rowSystemId] && (
            <Fragment>
              <a
                className="mini__icon mini__icon--accept"
                onClick={() => removeOrderRow(order)}
                title={translate('general.ok')}
              ></a>
              <a
                className="mini__icon mini__icon--cancel"
                onClick={() =>
                  setRemovingRow({
                    ...removingRow,
                    [order.rowSystemId]: false,
                  })
                }
                title={translate('general.cancel')}
              ></a>
            </Fragment>
          )}
        </Fragment>
      );
    },
    [removeOrderRow, removingRow]
  );

  const removeSorting = useCallback(
    (rowSystemIds) =>
      dispatch(removeRange(rowSystemIds)).then(() => dispatch(reloadPayment())),
    [dispatch]
  );

  const RemoveSortingBtn = useCallback(
    ({ sorting }) => {
      return (
        <Fragment>
          {!removingSorting[sorting.id] && (
            <a
              className="mini__icon mini__icon--delete"
              onClick={() =>
                setRemoveSorting({
                  ...removingSorting,
                  [sorting.id]: true,
                })
              }
              title={translate('general.remove')}
            ></a>
          )}
          {removingSorting[sorting.id] && (
            <Fragment>
              <a
                className="mini__icon mini__icon--accept"
                onClick={() =>
                  removeSorting(sorting.orderRows.map((r) => r.rowSystemId))
                }
                title={translate('general.ok')}
              ></a>
              <a
                className="mini__icon mini__icon--cancel"
                onClick={() =>
                  setRemoveSorting({
                    ...removingSorting,
                    [sorting.id]: false,
                  })
                }
                title={translate('general.cancel')}
              ></a>
            </Fragment>
          )}
        </Fragment>
      );
    },
    [removeSorting, removingSorting]
  );

  const handleQuantityInput = useCallback(
    (articleNumber, quantity, rowSystemId, ensureCorrectness = true) => {
      let floatQuantity = parseFloat(quantity);
      let validValue = !isNaN(floatQuantity) && floatQuantity > 0;
      if (ensureCorrectness) {
        quantity = validValue ? floatQuantity : 1;
        floatQuantity = quantity;
        validValue = true;
      }
      const index = orderRows.findIndex(
        (item) => item.rowSystemId === rowSystemId
      );
      const oldQuantity = parseFloat(orderRows[index].quantity);
      if (floatQuantity !== oldQuantity) {
        mapCartToAbort[articleNumber] && mapCartToAbort[articleNumber].abort();
        quantityDispatch && quantityDispatch.cancel();
        validValue && quantityDispatch(articleNumber, quantity, dispatch);
      }
      const tempOrderRows = [...orderRows];
      tempOrderRows[index] = {
        ...tempOrderRows[index],
        quantity,
      };
      setOrderRows(tempOrderRows);
    },
    [dispatch, orderRows]
  );

  const ProductQuantity = useCallback(
    (order) => {
      return (
        <Fragment>
          {order.isFreeGift ? (
            <div>{order.quantity}</div>
          ) : (
            <input
              className="checkout-cart__input"
              type="number"
              min="1"
              maxLength={3}
              value={order.quantity.toString()}
              onChange={(event) =>
                handleQuantityInput(
                  order.articleNumber,
                  event.target.value,
                  order.rowSystemId,
                  false
                )
              }
              onBlur={(event) =>
                handleQuantityInput(
                  order.articleNumber,
                  event.target.value,
                  order.rowSystemId
                )
              }
            />
          )}
        </Fragment>
      );
    },
    [handleQuantityInput]
  );

  const showItemCount =
    orderRows?.length > 0 || sortingRows?.length > 0 || decalRows?.length > 0;

  const getDecalEditorButton = (orderRow) => {
    if (window.__litium.decalEditorUrl) {
      return <a
        className="mini__icon mini__icon--edit"
        href={`${window.__litium.decalEditorUrl}?id=${orderRow.rowSystemId}`}
        title={translate('general.edit')}
      />
    }
    return <></>
  }

  const getEditSortingButton = (sorting) => {
    if (window.__litium.signEditorUrl) {
      return <a
        className="mini__icon mini__icon--edit"
        href={`${__litium.signEditorUrl}?sorting=${sorting.id}`}
        title={translate('general.edit')}
      />
    }
    return <></>
  }

  return (
    <div
      className={`cart cart--mini ${itemAddedAnimation ? 'cart--item-added' : ''
        }`}
    >
      <a
        className="cart__link--block"
        rel="nofollow"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          onToggle();
        }}
      >
        <i className="cart__icon">
          <span className="cart__quantity">{quantity}</span>
        </i>
        <span className="cart__title hide">
          {translate('minicart.checkout')}
        </span>
      </a>
      <div className={`cart__info ${!showInfo ? 'cart__info--hidden' : ''}`}>
        <div className="cart__header">
          <h2 className="cart__title-text">Din varukorg</h2>
          <span
            className="cart__close-button"
            onClick={() => onToggle()}
          ></span>
        </div>

        <div className="cart__content">
          {sortingRows?.length > 0 && (
            <div className="cart__section">
              <h3 className="cart__section-title">Sorteringar</h3>
              <div className="cart__sorting-list">
                {sortingRows.map((sorting) => {
                  const isEditing = sorting.id === editingRowSystemId;
                  return (
                    <div
                      key={sorting.id}
                      className={`cart__sorting-item ${isEditing ? 'cart__sorting-item--editing' : ''
                        }`}
                    >
                      <div className="cart__sorting-info">
                        <div className="cart__sorting-name">{sorting.name}</div>
                        <div className="cart__sorting-quantity">
                          {translate('minicart.quantity')}{' '}
                          {sorting.totalQuantity} - {sorting.sortingTotal}
                        </div>
                      </div>
                      <div className="cart__sorting-actions">
                        {getEditSortingButton(sorting)}
                        <RemoveSortingBtn sorting={sorting} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {decalRows?.length > 0 && (
            <div className="cart__section">
              <h3 className="cart__section-title">Dekaler</h3>
              <div className="cart__sorting-list">
                {decalRows.map((orderRow) => {
                  const isEditing = orderRow.rowSystemId === editingRowSystemId;

                  return (
                    <div
                      key={orderRow.rowSystemId}
                      className={`cart__sorting-item ${isEditing ? 'cart__sorting-item--editing' : ''
                        }`}
                    >
                      <div className="cart__sorting-info">
                        <div className="cart__sorting-name">
                          {orderRow.name}
                        </div>
                        <div className="cart__sorting-quantity">
                          {translate('minicart.quantity')} {orderRow.quantity} -{' '}
                          {orderRow.totalPrice}
                        </div>
                      </div>
                      <div className="cart__sorting-actions">
                        {getDecalEditorButton(orderRow)}
                        <RemoveBtn order={orderRow} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {orderRows?.length > 0 && (
            <div className="cart__section">
              <h3 className="cart__section-title">Produkter</h3>
              <div className="cart__product-list">
                {orderRows.map((orderRow) => (
                  <div
                    key={orderRow.rowSystemId}
                    className="cart__product-item"
                  >
                    <div className="cart__product-info">
                      <div className="cart__product-name">{orderRow.name}</div>
                      <div className="cart__product-price">
                        {orderRow.formattedPrice}
                      </div>
                    </div>
                    <div className="cart__product-actions">
                      <div className="cart__product-quantity">
                        {ProductQuantity(orderRow)}
                      </div>
                      <div>{orderRow.unitOfMeasurementTitle}</div>
                      <RemoveBtn order={orderRow} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!showItemCount && (
            <div className="cart__empty-message">
              {translate('minicart.empty_cart')}
            </div>
          )}
        </div>

        <div className="cart__footer">
          <div className="cart__summary">
            <div className="cart__total-items">
              <span>
                {quantity} {translate('minicart.numberofproduct')}
              </span>
            </div>
            <div className="cart__total-price">
              <span>{translate('minicart.total')}</span>
              <span className="cart__total-amount">{orderTotal}</span>
            </div>
          </div>
          <div className="cart__actions">
            <a href={checkoutUrl} className="cart__checkout-button">
              {translate('minicart.checkout')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MiniCart;
